import { collectUniqueStrategiesFromTrade } from "./tradeBlotterHelpers";

export const formatUsd = (value, { decimals = 2 } = {}) => {
  const num = Number(value) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

export const formatUsdNoDecimals = (value) => formatUsd(value, { decimals: 0 });

/** Display label for ticker dropdowns: `SYMBOL` or `SYMBOL/stock`. */
export const formatTickerSymbolDisplay = (ticker) => {
  if (!ticker?.symbol) return "";
  const sym = String(ticker.symbol).toUpperCase();
  const stock =
    ticker.stock != null && String(ticker.stock).trim() !== ""
      ? String(ticker.stock).trim().toUpperCase()
      : "";
  return stock ? `${sym}/${stock}` : sym;
};

/** Unique key for ticker options (symbol + optional stock leg). */
export const getTickerOptionKey = (ticker) => {
  const sym = (ticker?.symbol || "").toLowerCase();
  const stock = (ticker?.stock || "").toLowerCase().trim();
  return stock ? `${sym}/${stock}` : sym;
};

const isValidTickerSymbol = (symbol) =>
  Boolean(symbol && /^[A-Za-z]/.test(symbol));

const normalizeTickerStock = (stock) =>
  stock != null && String(stock).trim() !== "" ? String(stock) : "";

function addTickerDropdownOption(tickerSet, tickerObjects, source) {
  if (!isValidTickerSymbol(source?.symbol)) return;
  const stock = normalizeTickerStock(source.stock);
  const key = getTickerOptionKey({ symbol: source.symbol, stock });
  if (tickerSet.has(key)) return;
  tickerSet.add(key);
  tickerObjects.push({
    symbol: String(source.symbol).toUpperCase(),
    stock,
    investment: source.investment,
    currency: source.currency,
    exchange: source.exchange,
  });
}

/**
 * Builds sorted ticker dropdown options from lots, optional current order ticker (edit), and temp rows.
 */
export function buildTickerDropdownOptions({
  lots,
  orderTicker,
  extraRows = [],
} = {}) {
  const tickerSet = new Set();
  const tickerObjects = [];

  (lots || []).forEach((lot) => {
    if (lot?.ticker) addTickerDropdownOption(tickerSet, tickerObjects, lot.ticker);
  });

  if (orderTicker) {
    addTickerDropdownOption(tickerSet, tickerObjects, orderTicker);
  }

  (extraRows || []).forEach((row) => {
    addTickerDropdownOption(tickerSet, tickerObjects, row);
  });

  return tickerObjects.sort((a, b) =>
    formatTickerSymbolDisplay(a).localeCompare(formatTickerSymbolDisplay(b))
  );
}

/** Whole numbers with grouping; negatives as (1,234) */
export const formatSignedNumber = (value) => {
  const num = Number(value || 0);
  const absVal = Math.abs(num).toLocaleString("en-US");
  return num < 0 ? `(${absVal})` : absVal;
};

/** SellLong / SellShort: display in () and subtract in Total (NET). */
export const SUMMARY_OUTBOUND_ORDER_TYPES = new Set(["SellLong", "SellShort"]);

export const formatSummaryUsd = (value, { decimals = 2, parentheses = false } = {}) => {
  const num = Number(value) || 0;
  const formatted = formatUsd(Math.abs(num), { decimals });
  return num !== 0 && (parentheses || num < 0) ? `(${formatted})` : formatted;
};

/** Order types always shown in summary tables; zero-filled when absent */
export const TRADE_SUMMARY_FIXED_ORDER_TYPES = [
  "BuyLong",
  "SellLong",
  "SellShort",
  "CoverShort",
];

export const emptyTradeSummaryMetrics = () => ({
  noOfTradesMarket: 0,
  noOfTradesCrossing: 0,
  noOfTradesTotal: 0,
  sharesMarket: 0,
  sharesCrossing: 0,
  sharesTotal: 0,
  dollarsMarket: 0,
  dollarsCrossing: 0,
  dollarsTotal: 0,
});

const emptyOrderRow = (orderType) => ({
  orderType,
  ...emptyTradeSummaryMetrics(),
});

const METRIC_KEYS = Object.keys(emptyTradeSummaryMetrics());
const NET_METRIC_KEYS = METRIC_KEYS.filter((k) => !k.startsWith("noOfTrades"));

const applyTradeToMetrics = (target, filledAmount, liveValue, isCrossing) => {
  if (isCrossing) {
    target.noOfTradesCrossing += 1;
    target.sharesCrossing += liveValue;
    target.dollarsCrossing += filledAmount;
  } else {
    target.noOfTradesMarket += 1;
    target.sharesMarket += liveValue;
    target.dollarsMarket += filledAmount;
  }
  target.noOfTradesTotal += 1;
  target.sharesTotal += liveValue;
  target.dollarsTotal += filledAmount;
};

const rowHasTrades = (r) => r.noOfTradesTotal > 0;

/** Trade counts always sum; Dollars/Filled NET: − SellLong − SellShort */
const buildTotalRow = (rows) => {
  const t = emptyOrderRow("Total");
  rows.forEach((r) => {
    t.noOfTradesMarket += r.noOfTradesMarket;
    t.noOfTradesCrossing += r.noOfTradesCrossing;
    t.noOfTradesTotal += r.noOfTradesTotal;
    const sign = SUMMARY_OUTBOUND_ORDER_TYPES.has(r.orderType) ? -1 : 1;
    NET_METRIC_KEYS.forEach((k) => {
      t[k] += sign * (Number(r[k]) || 0);
    });
  });
  return t;
};

const emptyTradeBlotterSummary = () => {
  const fixedRows = TRADE_SUMMARY_FIXED_ORDER_TYPES.map((t) => emptyOrderRow(t));
  return {
    totalStrategies: 0,
    filledAmountLong: 0,
    filledAmountShort: 0,
    totalFilledAmount: 0,
    ordersByType: [...fixedRows, buildTotalRow(fixedRows)],
    strategyBreakdownByOrderType: null,
  };
};

/**
 * Aggregates trade rows for summary dialogs/tables (order type × market/crossing × counts / live notional / filled notional).
 * @param {boolean} collectStrategyBreakdown — when true (one fund, all strategies), include per-strategy breakdown per order type
 */
export const computeTradeBlotterSummary = (
  trades,
  livePrices,
  collectStrategyBreakdown = false
) => {
  if (!trades || trades.length === 0) {
    return emptyTradeBlotterSummary();
  }

  const priceBySymbol = {};
  (livePrices || []).forEach((p) => {
    if (!p || !p.symbol) return;
    const sym = p.symbol.toLowerCase();
    priceBySymbol[sym] = Number(p.price) || 0;
  });

  const uniqueStrategies = new Set();
  let filledAmountLong = 0;
  let filledAmountShort = 0;
  let totalFilledAmount = 0;
  const ordersByTypeMap = new Map();
  TRADE_SUMMARY_FIXED_ORDER_TYPES.forEach((t) => ordersByTypeMap.set(t, emptyOrderRow(t)));

  const strategyByOrderType = new Map();

  trades.forEach((trade) => {
    collectUniqueStrategiesFromTrade(trade).forEach((strategy) => {
      uniqueStrategies.add(strategy);
    });

    const filledAmount = trade.filledShares * trade.pricePerShare;
    totalFilledAmount += filledAmount;
    let orderType = "";
    const side = trade.side?.toLowerCase();

    if (side?.includes("buy long")) {
      orderType = "BuyLong";
      filledAmountLong += filledAmount;
    } else if (side?.includes("sell long")) {
      orderType = "SellLong";
    } else if (side?.includes("cover short")) {
      orderType = "CoverShort";
      filledAmountShort += filledAmount;
    } else if (side?.includes("sell short")) {
      orderType = "SellShort";
    }

    const isCrossing =
      trade.broker === "cros" ||
      trade.brokerAbbreviation === "cros" ||
      trade.custodian?.toLowerCase().includes("cross") ||
      trade.custodian === "CROSS";

    const mapKey =
      orderType && TRADE_SUMMARY_FIXED_ORDER_TYPES.includes(orderType) ? orderType : "__other__";
    if (mapKey === "__other__" && !ordersByTypeMap.has("__other__")) {
      ordersByTypeMap.set("__other__", emptyOrderRow("Other"));
    }
    const typeData = ordersByTypeMap.get(mapKey);

    const ticker = (trade.ticker || "").toLowerCase();
    const livePrice = priceBySymbol[ticker] || 0;
    const liveValue = livePrice * (trade.quantity || 0);

    applyTradeToMetrics(typeData, filledAmount, liveValue, isCrossing);

    if (
      collectStrategyBreakdown &&
      TRADE_SUMMARY_FIXED_ORDER_TYPES.includes(mapKey) &&
      trade.strategy &&
      trade.strategy !== "N/A"
    ) {
      if (!strategyByOrderType.has(mapKey)) {
        strategyByOrderType.set(mapKey, new Map());
      }
      const stratMap = strategyByOrderType.get(mapKey);
      if (!stratMap.has(trade.strategy)) {
        stratMap.set(trade.strategy, emptyTradeSummaryMetrics());
      }
      applyTradeToMetrics(stratMap.get(trade.strategy), filledAmount, liveValue, isCrossing);
    }
  });

  const fixedRows = TRADE_SUMMARY_FIXED_ORDER_TYPES.map((t) => ({
    ...ordersByTypeMap.get(t),
    orderType: t,
  }));
  const otherRow = ordersByTypeMap.get("__other__");
  const detailRows =
    otherRow && rowHasTrades(otherRow)
      ? [...fixedRows, { ...otherRow, orderType: "Other" }]
      : fixedRows;
  const totalRow = buildTotalRow(detailRows);

  const strategyBreakdownByOrderType = collectStrategyBreakdown
    ? Object.fromEntries(
        TRADE_SUMMARY_FIXED_ORDER_TYPES.map((ot) => {
          const m = strategyByOrderType.get(ot) || new Map();
          const list = Array.from(m.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([strategy, metrics]) => ({ strategy, ...metrics }));
          return [ot, list];
        })
      )
    : null;

  return {
    totalStrategies: uniqueStrategies.size,
    filledAmountLong,
    filledAmountShort,
    totalFilledAmount,
    ordersByType: [...detailRows, totalRow],
    strategyBreakdownByOrderType,
  };
};

/** SMA distribution legs use this bucket strategy id (CPFactorBW in `strategies`). */
export const SMA_BUCKET_STRATEGY_ID = "d16c4685f2e706f9ea6be3dc771b02e6";

export const getSmaBucketStrategyLabel = (strategies) => {
  const match = strategies?.find(
    (s) =>
      s?.strategyId === SMA_BUCKET_STRATEGY_ID ||
      s?.id === SMA_BUCKET_STRATEGY_ID
  );
  return match?.name || "CPFactorBW";
};

export const isFundSma = (fundId, managers) =>
  !!(fundId && managers?.[0]?.smas?.[fundId]);


/** Temporary extra symbols for ticker dropdown; remove when no longer needed. */
export const TEMP_TICKER_DROPDOWN_ROWS = [
  { symbol: "FTIXX", investment: 4, currency: 7, exchange: 4, stock:"a" },
  { symbol: "HLIEX", investment: 4, currency: 7, exchange: 4 },
  { symbol: "ITM", investment: 4, currency: 7, exchange: 4 },
  { symbol: "VTEB", investment: 4, currency: 7, exchange: 4 },
  { symbol: "F", investment: 4, currency: 7, exchange: 4 },
  { symbol: "Nok", investment: 4, currency: 7, exchange: 4 },
];